import React from "react";
import {Box, Tooltip} from "@mui/material";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import FaceOutlinedIcon from "@mui/icons-material/FaceOutlined";
import ChildCareOutlinedIcon from "@mui/icons-material/ChildCareOutlined";
import ChildFriendlyOutlinedIcon from "@mui/icons-material/ChildFriendlyOutlined";
import {alpha} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import {ElementChipList} from "./base/ElementChipList";
import {ElementChip} from "./base/ElementChip";

export function GuestsElement({
                               computedAgeCategories,
                               totalGuests = 0,
                               asPrint = false,
                           }: {
    computedAgeCategories: {
        adults?: number;
        teenagers?: number;
        children?: number;
        infants?: number;
    } | undefined;
    totalGuests?: number;
    asPrint?: boolean;
}) {
    const {t} = useTranslation();
    const adults = computedAgeCategories?.adults ?? 0;
    const teenagers = computedAgeCategories?.teenagers ?? 0;
    const children = computedAgeCategories?.children ?? 0;
    const infants = computedAgeCategories?.infants ?? 0;
    const hasAgeCategories = adults + teenagers + children + infants > 0;
    const uncategorizedGuests = hasAgeCategories ? 0 : totalGuests;

    if (!hasAgeCategories && !uncategorizedGuests) {
        return null;
    }

    const adultLabel = adults > 0
        ? `${adults} ${t(adults === 1 ? "Adult" : "Adults")}`
        : undefined;
    const teenagersLabel = teenagers > 0
        ? `${teenagers} ${t(teenagers === 1 ? "Teenager" : "Teenagers")}`
        : undefined;
    const childrenLabel = children > 0
        ? `${children} ${t(children === 1 ? "Child" : "Children")}`
        : undefined;
    const infantsLabel = infants > 0
        ? `${infants} ${t(infants === 1 ? "Infant" : "Infants")}`
        : undefined;
    const totalGuestsLabel = uncategorizedGuests > 0
        ? `${uncategorizedGuests} ${t(uncategorizedGuests === 1 ? "Guest" : "Guests")}`
        : undefined;

    if (asPrint) {
        return <>{[
            adultLabel,
            teenagersLabel,
            childrenLabel,
            infantsLabel,
            totalGuestsLabel,
        ].filter(Boolean).join(', ')}</>;
    }

    return (
        <ElementChipList>
            {adults > 0 && (
                <Tooltip title={adultLabel} arrow>
                    <Box component="span">
                        <ElementChip
                            text={`${adults}`}
                            startIcon={adults === 1
                                ? <PersonOutlinedIcon sx={{fontSize: 15, color: "#2563EB"}}/>
                                : <PeopleAltOutlinedIcon sx={{fontSize: 15, color: "#2563EB"}}/>}
                            borderColor={alpha("#2563EB", 0.24)}
                            backgroundColor={alpha("#2563EB", 0.06)}
                        />
                    </Box>
                </Tooltip>
            )}
            {teenagers > 0 && (
                <Tooltip title={teenagersLabel} arrow>
                    <Box component="span">
                        <ElementChip
                            text={`${teenagers}`}
                            startIcon={<FaceOutlinedIcon sx={{fontSize: 15, color: "#0891B2"}}/>}
                            borderColor={alpha("#0891B2", 0.28)}
                            backgroundColor={alpha("#0891B2", 0.06)}
                        />
                    </Box>
                </Tooltip>
            )}
            {children > 0 && (
                <Tooltip title={childrenLabel} arrow>
                    <Box component="span">
                        <ElementChip
                            text={`${children}`}
                            startIcon={<ChildCareOutlinedIcon sx={{fontSize: 15, color: "#D97706"}}/>}
                            borderColor={alpha("#D97706", 0.28)}
                            backgroundColor={alpha("#D97706", 0.06)}
                        />
                    </Box>
                </Tooltip>
            )}
            {infants > 0 && (
                <Tooltip title={infantsLabel} arrow>
                    <Box component="span">
                        <ElementChip
                            text={`${infants}`}
                            startIcon={(
                                <ChildFriendlyOutlinedIcon sx={{fontSize: 15, color: "#7C3AED"}}/>
                            )}
                            borderColor={alpha("#7C3AED", 0.28)}
                            backgroundColor={alpha("#7C3AED", 0.06)}
                        />
                    </Box>
                </Tooltip>
            )}
            {uncategorizedGuests > 0 && (
                <Tooltip title={totalGuestsLabel} arrow>
                    <Box component="span">
                        <ElementChip
                            text={`${uncategorizedGuests}`}
                            startIcon={uncategorizedGuests === 1
                                ? <PersonOutlinedIcon sx={{fontSize: 15, color: "#2563EB"}}/>
                                : <PeopleAltOutlinedIcon sx={{fontSize: 15, color: "#2563EB"}}/>}
                            borderColor={alpha("#2563EB", 0.24)}
                            backgroundColor={alpha("#2563EB", 0.06)}
                        />
                    </Box>
                </Tooltip>
            )}
        </ElementChipList>
    );
}
